<template>
  <ul class="flex items-center justify-center gap-1 list-none">
    <li>
      <a
        v-if="!isFirstPage"
        :class="[TEXT_LINK_CLASS, TEXT_LINK_ENABLED_CLASS]"
        :aria-label="$t('First')"
        :href="createUrl(1)"
        data-testid="paginationFirstPage"
        @click.prevent="goToPage(1)"
      >{{ $t('First') }}</a>
      <span
        v-else
        :class="[TEXT_LINK_CLASS, DISABLED_CLASS]"
        :aria-label="$t('First')"
      >{{ $t('First') }}</span>
    </li>
    <li>
      <a
        v-if="!isFirstPage"
        :class="[ARROW_CLASS, ARROW_ENABLED_CLASS, 'svg--chevron-left']"
        :aria-label="$t('Previous')"
        :href="createUrl(currentPage - 1)"
        data-testid="paginationPreviousPage"
        @click.prevent="goToPage(currentPage - 1)"
      />
      <button
        v-else
        :class="[ARROW_CLASS, DISABLED_CLASS, 'svg--chevron-left']"
        :aria-label="$t('Previous')"
        disabled
      />
    </li>
    <li
      v-for="page in pages"
      :key="page"
    >
      <a
        :class="[TEXT_LINK_CLASS, page === currentPage ? 'text-EXPONDO-primary-80 font-bold' : TEXT_LINK_ENABLED_CLASS]"
        :aria-label="`Page ${page}`"
        :href="createUrl(page)"
        data-testid="paginationPage"
        @click.prevent="goToPage(page)"
      >{{ page }}</a>
    </li>
    <li>
      <a
        v-if="!isLastPage"
        :class="[ARROW_CLASS, ARROW_ENABLED_CLASS, 'svg--chevron-right']"
        :aria-label="$t('Next')"
        :href="createUrl(currentPage + 1)"
        data-testid="paginationNextPage"
        @click.prevent="goToPage(currentPage + 1)"
      />
      <button
        v-else
        :class="[ARROW_CLASS, DISABLED_CLASS, 'svg--chevron-right']"
        :aria-label="$t('Next')"
        disabled
      />
    </li>
    <li>
      <a
        v-if="!isLastPage"
        :class="[TEXT_LINK_CLASS, TEXT_LINK_ENABLED_CLASS]"
        :aria-label="$t('Last')"
        :href="createUrl(totalPages)"
        data-testid="paginationLastPage"
        @click.prevent="goToPage(totalPages)"
      >{{ $t('Last') }}</a>
      <span
        v-else
        :class="[TEXT_LINK_CLASS, DISABLED_CLASS]"
        :aria-label="$t('Last')"
      >{{ $t('Last') }}</span>
    </li>
  </ul>
</template>

<script setup lang="ts">
// Looks like PaginationWrapper (ais-pagination), driven by props instead of InstantSearch.
import { getPaginationPages } from '~~/helpers/pagination/getPaginationPages';
import { scrollToSection } from '~~/helpers/scroll/scrollToSection';

defineOptions({ name: 'CtPagination' });

const PAGINATION_PADDING = 2;
const TEXT_LINK_CLASS = 'flex items-center h-9 px-2 rounded transition-colors';
const TEXT_LINK_ENABLED_CLASS = 'text-EXPONDO-primary-80 hover:bg-EXPONDO-primary-10';
// The chevron is a mask filled with the text colour, stretched over the whole box.
const ARROW_CLASS = 'iconify block w-9 h-9 transition-colors';
const ARROW_ENABLED_CLASS = 'text-EXPONDO-primary-80 hover:text-EXPONDO-primary';
const DISABLED_CLASS = 'text-EXPONDO-grey-60 cursor-not-allowed';

const props = defineProps<{
  currentPage: number;
  totalPages: number;
  createUrl: (page: number) => string;
}>();

const emit = defineEmits<{
  (event: 'page-change', page: number): void;
}>();

const isFirstPage = computed(() => props.currentPage <= 1);
const isLastPage = computed(() => props.currentPage >= props.totalPages);
const pages = computed(() => getPaginationPages({
  currentPage: props.currentPage,
  totalPages: props.totalPages,
  padding: PAGINATION_PADDING,
}));

const goToPage = (page: number) => {

  emit('page-change', page);
  scrollToSection('#layout');

};
</script>
